import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

export interface TimelineEntry {
  action: string;
  user_name: string;
  timestamp: string;
  pending_duration: string;
  comments: string;
  rejection_reason: string | null;
  cancellation_reason: string | null;
  assigned_at: string;
  action_taken_at: string;
}

// Legacy interface for backward compatibility
export interface LegacyApprovalStage {
  id: number;
  name: string;
  level: number;
  status: 'pending' | 'approved' | 'rejected' | 'not_started';
  approver_name?: string;
  approved_at?: string;
  comments?: string;
  is_current?: boolean;
}

export interface ApprovalStage {
  id: number;
  name: string;
  level: number;
  status: 'pending' | 'approved' | 'rejected' | 'not_started';
  approver_name?: string;
  approved_at?: string;
  comments?: string;
  is_current?: boolean;
  timeline: TimelineEntry[];
  pending_time_seconds: number;
  pending_time_formatted: string;
  cancellation_count: number;
}

export interface TimelineSummary {
  total_entries: number;
  assigned_entries: number;
  cancelled_entries: number;
  approved_entries: number;
  rejected_entries: number;
}

export interface EnhancedData {
  total_pending_time_seconds: number;
  total_pending_time_formatted: string;
  cancellation_count: number;
  timeline_summary: TimelineSummary;
}

export interface ApprovalTimelineData {
  document_id: number;
  document_type: string;
  current_stage: number;
  total_stages: number;
  stages: ApprovalStage[];
  created_at: string;
  created_by: string;
  overall_status: 'pending' | 'approved' | 'rejected';
  rejection_count?: number;
  last_rejected_level?: number | null;
  rejected_by?: string | null;
  rejected_by_name?: string | null;
  enhanced_data?: EnhancedData;
}

export interface RejectionDetail {
  level: number;
  date: string;
  rejectedBy: string;
  reason?: string;
  responseTime?: string;
}

@Component({
  selector: 'app-approval-timeline',
  templateUrl: './approval-timeline.component.html',
  styleUrls: ['./approval-timeline.component.scss'],
  standalone: true,
  imports: [CommonModule, MatIconModule]
})
export class ApprovalTimelineComponent implements OnInit {
  @Input() approvalData: ApprovalTimelineData;
  @Input() showHeader: boolean = true;
  @Input() compact: boolean = false;

  constructor() { }

  ngOnInit(): void {
    console.log('Approval Timeline Data:', this.approvalData);
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'danger';
      case 'pending': return 'warning';
      case 'not_started': return 'secondary';
      default: return 'secondary';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'approved': return 'check_circle';
      case 'rejected': return 'cancel';
      case 'pending': return 'pending';
      case 'not_started': return 'schedule';
      default: return 'schedule';
    }
  }

  getProgressPercentage(): number {
    if (!this.approvalData?.stages) return 0;
    const approvedStages = this.approvalData.stages.filter(stage => stage.status === 'approved').length;
    return (approvedStages / this.approvalData.stages.length) * 100;
  }

  getCurrentStage(): ApprovalStage | null {
    if (!this.approvalData?.stages) return null;
    return this.approvalData.stages.find(stage => stage.is_current) || 
           this.approvalData.stages.find(stage => stage.status === 'pending') || null;
  }

  getNextStage(): ApprovalStage | null {
    if (!this.approvalData?.stages) return null;
    const currentIndex = this.approvalData.stages.findIndex(stage => stage.is_current || stage.status === 'pending');
    if (currentIndex === -1 || currentIndex >= this.approvalData.stages.length - 1) return null;
    return this.approvalData.stages[currentIndex + 1];
  }

  hasRejectionInfo(): boolean {
    return !!(this.approvalData?.rejection_count && this.approvalData.rejection_count > 0);
  }

  getRejectionInfo(): any {
    if (!this.hasRejectionInfo()) return null;
    return {
      count: this.approvalData.rejection_count,
      level: this.approvalData.last_rejected_level,
      rejectedBy: this.approvalData.rejected_by_name || this.approvalData.rejected_by
    };
  }

  hasCancellationInfo(): boolean {
    return !!(this.approvalData?.enhanced_data?.cancellation_count && this.approvalData.enhanced_data.cancellation_count > 0);
  }

  getCancellationInfo(): any {
    if (!this.hasCancellationInfo()) return null;
    return {
      count: this.approvalData.enhanced_data?.cancellation_count || 0,
      totalTime: this.approvalData.enhanced_data?.total_pending_time_formatted || '0s'
    };
  }

  getTimelineSummary(): TimelineSummary | null {
    return this.approvalData?.enhanced_data?.timeline_summary || null;
  }

  getLatestTimelineEntry(stage: ApprovalStage): TimelineEntry | null {
    if (!stage.timeline || stage.timeline.length === 0) return null;
    return stage.timeline[stage.timeline.length - 1];
  }

  getActionIcon(action: string): string {
    switch (action.toLowerCase()) {
      case 'assigned': return 'assignment';
      case 'approved': return 'check_circle';
      case 'rejected': return 'cancel';
      case 'cancelled': return 'block';
      case 'resubmitted': return 'refresh';
      default: return 'info';
    }
  }

  getActionColor(action: string): string {
    switch (action.toLowerCase()) {
      case 'assigned': return 'primary';
      case 'approved': return 'success';
      case 'rejected': return 'danger';
      case 'cancelled': return 'warning';
      case 'resubmitted': return 'info';
      default: return 'secondary';
    }
  }

  formatDuration(duration: string): string {
    return duration || '0s';
  }

  hasTimelineData(stage: ApprovalStage): boolean {
    return stage.timeline && stage.timeline.length > 0;
  }

  // New methods for response time analytics
  getResponseTimeAnalytics(): boolean {
    return this.approvalData?.stages?.some(stage => 
      stage.status === 'approved' || stage.status === 'rejected'
    ) || false;
  }

  getAverageResponseTime(): string {
    if (!this.approvalData?.stages) return '';
    
    const completedStages = this.approvalData.stages.filter(stage => 
      stage.status === 'approved' || stage.status === 'rejected'
    );
    
    if (completedStages.length === 0) return '';
    
    const totalSeconds = completedStages.reduce((sum, stage) => sum + stage.pending_time_seconds, 0);
    const averageSeconds = totalSeconds / completedStages.length;
    
    return this.formatTimeFromSeconds(averageSeconds);
  }

  getFastestResponseTime(): string {
    if (!this.approvalData?.stages) return '';
    
    const completedStages = this.approvalData.stages.filter(stage => 
      stage.status === 'approved' || stage.status === 'rejected'
    );
    
    if (completedStages.length === 0) return '';
    
    const fastestStage = completedStages.reduce((fastest, stage) => 
      stage.pending_time_seconds < fastest.pending_time_seconds ? stage : fastest
    );
    
    return fastestStage.pending_time_formatted;
  }

  getSlowestResponseTime(): string {
    if (!this.approvalData?.stages) return '';
    
    const completedStages = this.approvalData.stages.filter(stage => 
      stage.status === 'approved' || stage.status === 'rejected'
    );
    
    if (completedStages.length === 0) return '';
    
    const slowestStage = completedStages.reduce((slowest, stage) => 
      stage.pending_time_seconds > slowest.pending_time_seconds ? stage : slowest
    );
    
    return slowestStage.pending_time_formatted;
  }

  getStageResponseTime(stage: ApprovalStage): string {
    if (stage.status === 'not_started' || stage.status === 'pending') return '';
    return stage.pending_time_formatted;
  }

  getResponseTimeClass(stage: ApprovalStage): string {
    if (!stage.pending_time_seconds) return '';
    
    const hours = stage.pending_time_seconds / 3600;
    if (hours <= 1) return 'response-fast';
    if (hours <= 4) return 'response-medium';
    return 'response-slow';
  }

  formatTimeFromSeconds(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }

  // Enhanced rejection tracking
  getDetailedRejections(): RejectionDetail[] {
    if (!this.approvalData?.stages) return [];
    
    const rejections: RejectionDetail[] = [];
    
    this.approvalData.stages.forEach(stage => {
      if (stage.status === 'rejected') {
        const rejectionEntry = stage.timeline?.find(entry => 
          entry.action.toLowerCase() === 'rejected'
        );
        
        if (rejectionEntry) {
          rejections.push({
            level: stage.level,
            date: rejectionEntry.timestamp,
            rejectedBy: rejectionEntry.user_name,
            reason: rejectionEntry.rejection_reason || undefined,
            responseTime: stage.pending_time_formatted
          });
        }
      }
    });
    
    return rejections.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  // Get all users who have taken actions
  getAllActionUsers(): string[] {
    if (!this.approvalData?.stages) return [];
    
    const users = new Set<string>();
    
    this.approvalData.stages.forEach(stage => {
      stage.timeline?.forEach(entry => {
        if (entry.user_name) {
          users.add(entry.user_name);
        }
      });
    });
    
    return Array.from(users);
  }

  // Get response time distribution
  getResponseTimeDistribution(): { fast: number; medium: number; slow: number } {
    if (!this.approvalData?.stages) return { fast: 0, medium: 0, slow: 0 };
    
    const completedStages = this.approvalData.stages.filter(stage => 
      stage.status === 'approved' || stage.status === 'rejected'
    );
    
    let fast = 0, medium = 0, slow = 0;
    
    completedStages.forEach(stage => {
      const hours = stage.pending_time_seconds / 3600;
      if (hours <= 1) fast++;
      else if (hours <= 4) medium++;
      else slow++;
    });
    
    return { fast, medium, slow };
  }
} 